import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    Boxes,
    Camera,
    CheckCircle2,
    ClipboardCheck,
    Cog,
    ExternalLink,
    FileText,
    History as HistoryIcon,
    Loader2,
    PackageCheck,
    Receipt,
    Stethoscope,
    User,
    Wrench,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { WorkshopStatusBadge } from "@/components/common/WorkshopStatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

import { workshopService } from "@/services/workshop.service";
import { proposalsService } from "@/services/proposals.service";
import { getErrorMessage } from "@/services/api";
import { formatDate, formatDateTime } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";

import { ReceiveEquipmentDialog } from "./components/ReceiveEquipmentDialog";
import { WorkshopChecklist } from "./components/WorkshopChecklist";
import { DiagnosisForm } from "./components/DiagnosisForm";
import {
    RequiredServicesEditor,
    RequiredPartsEditor,
} from "./components/ServicesPartsEditors";
import { TechnicalReportForm } from "./components/TechnicalReportForm";
import { WorkshopPhotoUploader } from "./components/WorkshopPhotoUploader";
import { WorkshopTimeline } from "./components/WorkshopTimeline";

function InfoBlock({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className="text-sm text-foreground">{value ?? "—"}</div>
        </div>
    );
}

export default function WorkshopDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = usePermission();

    const [receiveOpen, setReceiveOpen] = useState(false);

    const wsQuery = useQuery({
        queryKey: ["workshop", id],
        queryFn: () => workshopService.get(id),
        enabled: !!id,
    });

    const finish = useMutation({
        mutationFn: () => workshopService.finish(id),
        onSuccess: () => {
            toast.success("Diagnóstico finalizado. Aguardando proposta comercial.");
            qc.invalidateQueries({ queryKey: ["workshop", id] });
            qc.invalidateQueries({ queryKey: ["workshop-dashboard"] });
            qc.invalidateQueries({ queryKey: ["workshops"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao finalizar diagnóstico.")),
    });

    const createProposal = useMutation({
        mutationFn: () => proposalsService.createFromWorkshop(id),
        onSuccess: (proposal) => {
            toast.success(`Proposta ${proposal.number} pronta para edição.`);
            qc.invalidateQueries({ queryKey: ["proposals-dashboard"] });
            qc.invalidateQueries({ queryKey: ["proposals"] });
            navigate(`/proposals/${proposal.id}`);
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao criar proposta comercial.")),
    });

    if (wsQuery.isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-72" />
                <div className="grid gap-4 md:grid-cols-3">
                    <Skeleton className="h-24" />
                    <Skeleton className="h-24" />
                    <Skeleton className="h-24" />
                </div>
                <Skeleton className="h-96" />
            </div>
        );
    }

    if (wsQuery.isError || !wsQuery.data) {
        return (
            <div className="rounded-md border border-dashed p-8 text-center">
                <div className="text-sm font-medium">Registro de oficina não encontrado.</div>
                <Button variant="ghost" className="mt-3" onClick={() => navigate("/workshop")}>
                    Voltar para a lista
                </Button>
            </div>
        );
    }

    const ws = wsQuery.data;
    const asset = ws.asset || {};
    const customer = ws.customer || {};
    const canReceive = ws.workshop_status === "aguardando_recebimento";
    const canFinish = ws.workshop_status === "em_diagnostico";
    const canCreateProposal =
        ws.workshop_status === "aguardando_proposta" && can("commercial_proposals", "create");

    return (
        <div className="space-y-6" data-testid="workshop-details-page">
            <PageHeader
                title={`Oficina · ${ws.work_order_number}`}
                description={`Registro criado em ${ws.created_at ? formatDate(ws.created_at) : "—"}.`}
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Oficina", to: "/workshop" },
                    { label: ws.work_order_number },
                ]}
                backTo="/workshop"
                actions={
                    <>
                        {canReceive && (
                            <Button
                                onClick={() => setReceiveOpen(true)}
                                className="bg-secondary text-secondary-foreground hover:opacity-90"
                                data-testid="open-receive-dialog"
                            >
                                <PackageCheck className="mr-2 h-4 w-4" />
                                Receber equipamento
                            </Button>
                        )}
                        {canFinish && (
                            <Button
                                onClick={() => finish.mutate()}
                                disabled={finish.isPending}
                                className="bg-indigo-600 text-white hover:bg-indigo-700"
                                data-testid="finish-workshop-button"
                            >
                                {finish.isPending
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <CheckCircle2 className="mr-2 h-4 w-4" />}
                                Finalizar diagnóstico
                            </Button>
                        )}
                        {canCreateProposal && (
                            <Button
                                onClick={() => createProposal.mutate()}
                                disabled={createProposal.isPending}
                                className="bg-brand-navy text-primary-foreground hover:opacity-90"
                                data-testid="create-proposal-from-workshop"
                            >
                                {createProposal.isPending
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <Receipt className="mr-2 h-4 w-4" />}
                                Criar proposta comercial
                            </Button>
                        )}
                    </>
                }
            />

            {/* Header cards */}
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_320px]">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                            <Cog className="h-4 w-4 text-muted-foreground" />
                            Equipamento
                        </CardTitle>
                        <WorkshopStatusBadge value={ws.workshop_status} size="lg" />
                    </CardHeader>
                    <CardContent className="grid gap-3 md:grid-cols-3">
                        <InfoBlock label="Código" value={asset.internal_code || ws.asset_snapshot_internal_code} />
                        <InfoBlock label="Categoria" value={asset.category || ws.asset_snapshot_category} />
                        <InfoBlock label="Fabricante" value={asset.manufacturer || ws.asset_snapshot_manufacturer} />
                        <InfoBlock label="Modelo" value={asset.model || ws.asset_snapshot_model} />
                        <InfoBlock label="Nº série" value={asset.serial_number || ws.asset_snapshot_serial_number} />
                        <InfoBlock label="Patrimônio" value={asset.customer_asset_tag} />
                        <InfoBlock label="Potência" value={asset.power} />
                        <InfoBlock label="Tensão" value={asset.voltage} />
                        <InfoBlock label="RPM" value={asset.rpm} />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Cliente & OS</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Cliente</div>
                            <div className="font-medium">{customer.legal_name || "—"}</div>
                            {customer.trade_name && <div className="text-xs text-muted-foreground">{customer.trade_name}</div>}
                            {customer.phone && <div className="text-xs text-muted-foreground">{customer.phone}</div>}
                        </div>
                        <div className="space-y-1">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ordem de Serviço</div>
                            <Link
                                to={`/work-orders/${ws.work_order_id}`}
                                className="inline-flex items-center gap-1 font-mono text-sm text-secondary hover:underline"
                                data-testid="workshop-open-wo"
                            >
                                {ws.work_order_number} <ExternalLink className="h-3 w-3" />
                            </Link>
                        </div>
                        {ws.arrival?.arrival_date && (
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Chegou em</div>
                                <div className="text-xs">{formatDateTime(ws.arrival.arrival_date)}</div>
                                {ws.arrival.general_condition && (
                                    <Badge variant="outline" className="mt-1 text-[10px]">
                                        {ws.arrival.general_condition}
                                    </Badge>
                                )}
                            </div>
                        )}
                        {ws.arrival?.initial_observations && (
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Observações iniciais</div>
                                <div className="text-xs text-muted-foreground">{ws.arrival.initial_observations}</div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="checklist" className="w-full">
                <TabsList className="flex-wrap justify-start" data-testid="workshop-tabs">
                    <TabsTrigger value="checklist" data-testid="tab-checklist">
                        <ClipboardCheck className="mr-2 h-4 w-4" /> Checklist
                    </TabsTrigger>
                    <TabsTrigger value="diagnosis" data-testid="tab-diagnosis">
                        <Stethoscope className="mr-2 h-4 w-4" /> Diagnóstico
                    </TabsTrigger>
                    <TabsTrigger value="services-parts" data-testid="tab-services-parts">
                        <Wrench className="mr-2 h-4 w-4" /> Serviços & Peças
                    </TabsTrigger>
                    <TabsTrigger value="report" data-testid="tab-report">
                        <FileText className="mr-2 h-4 w-4" /> Laudo técnico
                    </TabsTrigger>
                    <TabsTrigger value="photos" data-testid="tab-photos">
                        <Camera className="mr-2 h-4 w-4" /> Fotos ({(ws.photos || []).length})
                    </TabsTrigger>
                    <TabsTrigger value="timeline" data-testid="tab-timeline">
                        <HistoryIcon className="mr-2 h-4 w-4" /> Histórico
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="checklist" className="pt-4">
                    <Card>
                        <CardContent className="pt-6">
                            <WorkshopChecklist workshop={ws} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="diagnosis" className="pt-4">
                    <Card>
                        <CardContent className="pt-6">
                            <DiagnosisForm workshop={ws} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="services-parts" className="pt-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card>
                            <CardContent className="pt-6">
                                <RequiredServicesEditor workshop={ws} />
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <RequiredPartsEditor workshop={ws} />
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="report" className="pt-4">
                    <Card>
                        <CardContent className="pt-6">
                            <TechnicalReportForm workshop={ws} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="photos" className="pt-4">
                    <Card>
                        <CardContent className="pt-6">
                            <WorkshopPhotoUploader workshop={ws} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="timeline" className="pt-4">
                    <Card>
                        <CardContent className="pt-6">
                            <WorkshopTimeline workshop={ws} />
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <ReceiveEquipmentDialog
                open={receiveOpen}
                onOpenChange={setReceiveOpen}
                workshopId={id}
            />
        </div>
    );
}
